import React, { useEffect, useRef } from 'react';
import { Box, Flex, Text, Image, Link, Icon } from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { FormattedMessage } from 'react-intl';
import { SupportMessage } from 'components/SupportChat/hooks/useSupportChat';

const Attachment: React.FC<{ msg: SupportMessage }> = ({ msg }) => {
  if (!msg.attachment || !msg.attachmentSrc) return null;
  if (msg.attachment.kind === 'image') {
    return (
      <Link href={msg.attachmentSrc} isExternal>
        <Image
          src={msg.attachmentSrc}
          alt={msg.attachment.name || 'image'}
          maxW="220px"
          maxH="220px"
          borderRadius="md"
          mb={msg.body ? 1 : 0}
          loading="lazy"
        />
      </Link>
    );
  }
  return (
    <Link
      href={msg.attachmentSrc}
      isExternal
      display="inline-flex"
      alignItems="center"
      gap={1}
      fontSize="sm"
      textDecoration="underline"
      mb={msg.body ? 1 : 0}
    >
      <Icon as={AttachmentIcon} boxSize={3} />
      {msg.attachment.name || 'file'}
    </Link>
  );
};

interface MessageListProps {
  messages: SupportMessage[];
}

/** Bubble roles (all on the opaque bg.overlay panel): the visitor's own
 *  message on the success fill (green.500 under white was 3.25:1), the
 *  operator on neutral.200, a system notice in secondary text on neutral.100
 *  with a border.subtle outline — neutral.100 equals the overlay in dark, so
 *  the outline is its only edge there. */
const bubbleStyle = (sender: SupportMessage['sender']) => {
  if (sender === 'visitor') {
    return { bg: 'status.success', color: 'status.successFg', alignSelf: 'flex-end' };
  }
  if (sender === 'system') {
    return {
      bg: 'neutral.100',
      color: 'text.secondary',
      alignSelf: 'center',
      borderWidth: '1px',
      borderColor: 'border.subtle',
    };
  }
  return { bg: 'neutral.200', color: 'text.primary', alignSelf: 'flex-start' };
};

const MessageList: React.FC<MessageListProps> = ({ messages }) => {
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Flex direction="column" gap={2} p={3} overflowY="auto" flex="1">
      {messages.length === 0 && (
        <Text fontSize="sm" color="text.secondary" textAlign="center" mt={4}>
          <FormattedMessage id="support.greeting" />
        </Text>
      )}
      {messages.map((msg) => {
        const style = bubbleStyle(msg.sender);
        return (
          <Box
            key={msg.localId}
            maxW="80%"
            px={3}
            py={2}
            borderRadius="lg"
            fontSize="sm"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            {...style}
          >
            <Attachment msg={msg} />
            {msg.body}
          </Box>
        );
      })}
      <div ref={endRef} />
    </Flex>
  );
};

export default MessageList;
